import React, { useCallback, useContext, useEffect } from 'react'
import { InstantWinController } from 'components/instant-win/InstantWinController'
import {
  getInstantWin,
  qualifyingEvent,
  createEntry,
  getWheelConfig,
} from 'services/instant-win'
import IframeScript from 'components/NavBar/IframeScript'
import FooterLink from './FooterLink'
import analytics from 'global/AnalyticsData'
import {
  getProfile,
  getUserType,
  userContext,
} from 'global/context/sub-contexts/userContext'
import { userTypeEnum } from 'services/user'

import { getNextStep } from '../utils/stepHelpers'
import { useBreakData } from '../hooks/useBreakData'
import { useStepStatus } from '../hooks/useStepStatus'
import { useStepNavigation } from '../hooks/useStepNavigation'
import { useUserAuth } from '../hooks/useUserAuth'
import { useStepAnalytics } from '../hooks/useStepAnalytics'
import { useUrlParams } from '../hooks/useUrlParams'
import { useGating } from '../hooks/useGating'
import { BreakThemeProvider } from './BreakThemeProvider'
import { BreakNavigation } from './BreakNavigation'
import { GateBanner } from './GateBanner'
import { LandingStepContainer } from './LandingStepContainer'
import { StepList } from './StepList'

/**
 * The break rendering guts, WITHOUT the site <Layout> (header/footer).
 * Used by:
 *  - ScrollingSponsoredTemplate (the /break/{slug} and /embed/{slug} pages),
 *    which wrap this in <Layout>.
 *  - the Puck "Break" block, which renders it inline inside a page that
 *    already supplies its own Layout.
 *
 * `rawBrk` is the raw break JSON (same shape passed as pageContext.brk).
 */
export const BreakExperience = ({
  brk: rawBrk,
  isEmbed = false,
  // Rendered inside a page that supplies its own header and footer, rather
  // than as the whole page. Only affects layout, not the auth flow.
  inline = false,
}) => {
  const context = useContext(userContext)

  const brk = useBreakData(rawBrk, isEmbed)

  const { userType, handleAuthRequired, handleAttemptStepStart } =
    useUserAuth(isEmbed)

  const {
    stepStatusArr,
    getIsLocked,
    getIsComplete,
    getIsScrollComplete,
    unlockNextStep,
    scrollCompleteStep,
    markStepComplete,
    markStepSkipped,
  } = useStepStatus(brk, userType)

  const {
    currentPosition,
    currentStepIdRef,
    scrollToStep,
    updateCurrentStep,
    handleSideNavItemClick,
  } = useStepNavigation(brk)

  const { trackStepView, trackStepStart, trackStepComplete, trackStepSkip } =
    useStepAnalytics(brk)

  const {
    stepLocked,
    setStepLocked,
    showRelatedBreaks,
    relatedBreaks,
    handleNoClick,
    handleYesClick,
  } = useGating(brk)

  // Mirrors componentDidMount analytics init
  useEffect(() => {
    if (!brk) return
    analytics.setBreak(brk)
    analytics.setUser(getProfile())
    analytics.breakEvent('break_view', brk)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Mirrors componentDidUpdate — keep analytics user in sync across renders
  useEffect(() => {
    analytics.setUser(getProfile())
  })

  useUrlParams(brk?.steps, scrollToStep)

  // ─── Composed callbacks ───────────────────────────────────────────────────

  const triggerStepViewEventIfUnlocked = useCallback(
    (stepId, stepPosition) => {
      if (stepId === 'landing') return

      const scrollCompletePrevIfNeeded = () => {
        const prevIndex = stepPosition - 1
        if (prevIndex >= 0) {
          const prevStep = stepStatusArr[prevIndex]
          if (prevStep && !prevStep.complete && !prevStep.locked) {
            scrollCompleteStep(prevStep.id)
          }
        }
      }

      if (!getIsLocked(stepId) && !isEmbed) {
        scrollCompletePrevIfNeeded()
        trackStepView(stepId)
      } else if (isEmbed) {
        trackStepView(stepId)
      }
    },
    [stepStatusArr, getIsLocked, scrollCompleteStep, isEmbed, trackStepView],
  )

  const handleIntersect = useCallback(
    (stepId, isIntersecting) => {
      if (isIntersecting === true) {
        const step = brk.steps.find(s => s.id === stepId)
        const position = step
          ? brk.hasLandingScreen
            ? step.position
            : step.position - 1
          : 0

        if (currentStepIdRef.current !== stepId) {
          updateCurrentStep(position, stepId)
          triggerStepViewEventIfUnlocked(stepId, position)
        }
      }
    },
    [brk, currentStepIdRef, updateCurrentStep, triggerStepViewEventIfUnlocked],
  )

  const handleGoToNext = useCallback(
    stepId => {
      console.debug('handleGoToNext ' + stepId)
      if (!stepId) {
        console.error('Missing stepId parameter')
        return
      }
      const step = brk.steps.find(s => s.id === stepId)
      const nextStepPosition = step.position + (brk.hasLandingScreen ? 1 : 0)
      const nextStep = getNextStep(brk.steps, stepId)
      if (!nextStep) return
      scrollToStep(nextStepPosition, nextStep.id)
    },
    [brk, scrollToStep],
  )

  const handleStepStart = useCallback(
    (step, attempt = 1, isBreakEngagement = false) => {
      trackStepStart(step, attempt, isBreakEngagement)
    },
    [trackStepStart],
  )

  const handleStepComplete = useCallback(
    completedStepId => {
      markStepComplete(completedStepId)
      trackStepComplete(completedStepId)
    },
    [markStepComplete, trackStepComplete],
  )

  const handleStepSkip = useCallback(
    stepId => {
      console.log('handleStepSkip')
      trackStepSkip(stepId)
      markStepSkipped(stepId)
      unlockNextStep(stepId)
      handleGoToNext(stepId)
    },
    [trackStepSkip, markStepSkipped, unlockNextStep, handleGoToNext],
  )

  const handleLandingStartClick = useCallback(() => {
    const currentUserType = getUserType()
    if (
      currentUserType === userTypeEnum.NONE ||
      (currentUserType === userTypeEnum.GUEST && !isEmbed)
    ) {
      context.askToLogin()
    } else {
      scrollToStep(1, brk.steps[0].id)
    }
  }, [isEmbed, context, brk, scrollToStep])

  const handleAwardPoints = useCallback(
    points => {
      context.setPoints(points)
    },
    [context],
  )

  // ─── Render ───────────────────────────────────────────────────────────────

  if (!brk) return <></>

  return (
    <>
      <div id="fb-root" />
      <BreakThemeProvider brk={brk} isEmbed={isEmbed} inline={inline} />
      {/* Syncs height with an embedding parent via postMessage, and writes
          --content-min-height from what that parent reports. Inline there is no
          such parent, and letting it run would let an outer frame drive the
          step height of a page that sizes itself. */}
      {!inline && <IframeScript />}
      {stepLocked ? (
        <GateBanner
          brk={brk}
          onUnlock={() => setStepLocked(false)}
          onNoClick={handleNoClick}
          onYesClick={handleYesClick}
        />
      ) : (
        <BreakNavigation
          navigationType={brk.navigationType}
          stepStatusArr={stepStatusArr}
          activeStepPosition={currentPosition}
          onItemClicked={handleSideNavItemClick}
          brk={brk}
          inline={inline}
        />
      )}
      <InstantWinController
        brk={brk}
        permalink={brk.permalink}
        instantWinId={brk.instantWinId}
        userType={getUserType()}
        getInstantWin={getInstantWin}
        qualifyingEvent={qualifyingEvent}
        createEntry={createEntry}
        getWheelConfig={getWheelConfig}
        render={(
          instantWinLoaded = false,
          instantWinModal,
          handleInstantWinEvent = null,
        ) => (
          <>
            <main className="sponsored">
              {brk.hasLandingScreen !== false && (
                <LandingStepContainer
                  brk={brk}
                  isEmbed={isEmbed}
                  stepLocked={stepLocked}
                  showRelatedBreaks={showRelatedBreaks}
                  relatedBreaks={relatedBreaks}
                  onStartClick={handleLandingStartClick}
                  onAttemptStart={handleAttemptStepStart}
                  onIntersect={handleIntersect}
                />
              )}
              {!stepLocked && (
                <StepList
                  brk={brk}
                  isEmbed={isEmbed}
                  userType={userType}
                  getIsLocked={getIsLocked}
                  getIsComplete={getIsComplete}
                  getIsScrollComplete={getIsScrollComplete}
                  unlockNextStep={unlockNextStep}
                  onGoToNext={handleGoToNext}
                  onStepStart={handleStepStart}
                  onStepComplete={handleStepComplete}
                  onStepSkip={handleStepSkip}
                  onAuthRequired={handleAuthRequired}
                  onAwardPoints={handleAwardPoints}
                  onAttemptStepStart={handleAttemptStepStart}
                  onIntersect={handleIntersect}
                  handleInstantWinEvent={handleInstantWinEvent}
                />
              )}
            </main>
            {instantWinModal}
            {isEmbed && brk.footerLinks && (
              <div className="embed-footer">
                {brk.footerLinks.map((item, index) => (
                  <FooterLink key={index} item={item} />
                ))}
              </div>
            )}
          </>
        )}
      />
    </>
  )
}

export default BreakExperience
